import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { auth, app, API_BASE_URL } from '../../firebase/firebaseConfig';
import ErrorModal from '../ui/ErrorModal';
import {
  StageShell,
  HCard,
  HearthIcon,
  HigloIcon,
  HChip,
  SerifHeading,
  SectionLabel,
  FieldLabel,
  HField,
  HPrimaryButton,
  HSecondaryButton,
  HGhostButton,
  HButtonPair,
} from '../h';
import { PhilosophyWelcome } from './PhilosophyWelcome';
import { cn } from '../../lib/utils';

/* v9 mockups 2.1 — 2.4. Stage 1: "Set up your account".

   Behaviour preserved from the old AccountSetup:
   - hearthOwner flow: collect name → numOfFamilies-many Hearth names → first Higlo name
     → POST /user/setup; backend creates familyInfo docs, controllers, default igloo.
   - invitedUser flow: collect name only → POST /user/setup; backend wires the invited
     user as a controller of the family they were invited to.
   - /setup payload: { email, name, familyNames, userType, familyIDs?, iglooName? }
   - Pre-populate name from `nameFromServer || nameAtOrder || ''`.

   New behaviour:
   - Optional Philosophy welcome (v9 1.8) is shown as the very first screen for
     hearthOwners (skipped for invitedUser, since the philosophy is about
     owning a Hearth/Higlo). It uses the same `accountStatus !== setupCompleted`
     gate that already routes us into AccountSetup, so no extra check needed.
   - The wizard renders inside StageShell so the "Stage 1 of 2" indicator is
     consistent across all three steps. After submit, step 4 swaps to a "Stage 1
     done" summary with a "Take me to my Hearths" CTA. */

function StepCounter({ index, total }) {
  return (
    <div className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
      Step {index} of {total}
    </div>
  );
}

function AccountSetup({
  email,
  userType,
  familyIDs,
  familyName,
  numOfFamilies,
  nameFromServer,
  nameAtOrder,
  onSetupComplete,
  // Preview-only: jump straight to a specific step with seed data so the
  // screenshot script can capture each step in isolation. Production callers
  // should leave these undefined.
  initialStep,
  initialFamilyNames,
  initialIglooName,
}) {
  const isInvited = userType === 'invitedUser';
  const initialName = nameFromServer || nameAtOrder || '';

  const [name, setName] = useState(initialName);
  const [familyNames, setFamilyNames] = useState(initialFamilyNames || []);
  const [iglooName, setIglooName] = useState(initialIglooName || '');

  /* Step machine.
       0  philosophy (hearthOwner only — first thing they see)
       1  name
       2  Hearth names (hearthOwner only)
       3  first Higlo name (hearthOwner only)
       4  Navigating Higlos — Normal vs Auto explainer (hearthOwner only)
       5  done summary (hearthOwner only — invited goes straight to dashboard)
  */
  const [step, setStep] = useState(initialStep != null ? initialStep : isInvited ? 1 : 0);

  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* Initialise the per-Hearth name slots whenever the user type or family
     count changes. For invited users there's exactly one slot — the family
     they were invited to (we already have the family's name, so we can
     pre-populate it for clarity in the form).
     Skipped when `initialFamilyNames` is provided (preview/test mode). */
  useEffect(() => {
    if (initialFamilyNames) return;
    if (isInvited) {
      setFamilyNames([familyName || '']);
    } else {
      setFamilyNames(Array(numOfFamilies).fill(''));
    }
  }, [isInvited, familyName, numOfFamilies, initialFamilyNames]);

  const handleSignOut = async () => {
    try {
      await signOut(getAuth(app));
      window.location.reload();
    } catch (err) {
      console.error('sign out error', err);
    }
  };

  const submitSetup = async () => {
    if (!userType) {
      setErrorMessage('Setup state is missing. Please reload and try again.');
      setShowErrorModal(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const currentUser = auth.currentUser;
      const idToken = await currentUser.getIdToken();
      const requestBody = { email, name, familyNames, userType };
      if (!isInvited && iglooName.trim()) {
        requestBody.iglooName = iglooName.trim();
      }
      if (isInvited) {
        requestBody.familyIDs = familyIDs;
      }
      const response = await fetch(`${API_BASE_URL}/user/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (data.success) {
        if (isInvited) {
          // Invited users skip the done summary — they're already a member,
          // they just got their name on file. Go straight to dashboard.
          onSetupComplete();
        } else {
          setStep(5);
        }
      } else {
        setErrorMessage(data.error || 'Setup failed. Please try again.');
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('setup error', err);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onNameContinue = () => {
    if (!name.trim()) {
      setErrorMessage('Please enter your name.');
      setShowErrorModal(true);
      return;
    }
    if (isInvited) {
      submitSetup();
    } else {
      setStep(2);
    }
  };

  const onHearthsContinue = () => {
    if (familyNames.some((f) => !f.trim())) {
      setErrorMessage('Please give every Hearth a name.');
      setShowErrorModal(true);
      return;
    }
    setStep(3);
  };

  const onHigloContinue = () => {
    if (!iglooName.trim()) {
      setErrorMessage('Please give your first Higlo a name.');
      setShowErrorModal(true);
      return;
    }
    setStep(4);
  };

  const greetingFirstName = useMemo(() => {
    const trimmed = (name || '').trim();
    return trimmed ? trimmed.split(/\s+/)[0] : '';
  }, [name]);

  const totalSteps = isInvited ? 1 : 4;
  // Mockup 2.1/2.2/2.3/2.4 all show a centered-only logo at the top — no
  // top-right Sign-out. Step 1 has Sign-out in the footer button pair; the
  // other steps have Back. Keep the top header clean.
  const stageRightSlot = null;

  /* ───────── Step 0 — Philosophy welcome (hearthOwner only) ───────── */
  if (step === 0 && !isInvited) {
    return <PhilosophyWelcome onContinue={() => setStep(1)} onSignOut={handleSignOut} />;
  }

  /* ───────── Step 1 — Your name ───────── */
  if (step === 1) {
    return (
      <StageShell
        stageNumber={1}
        stageLabel="Stage 1 of 2"
        stageTitle="Set up your account"
        rightSlot={stageRightSlot}
      >
        <div className="px-4 pb-6">
          <StepCounter index={1} total={totalSteps} />
          <SerifHeading size="xl" className="mb-3">
            What should we call you?
          </SerifHeading>
          <HCard variant="muted" className="p-3 mb-4">
            <p className="text-[12px] text-slate-600 leading-relaxed">
              {isInvited ? (
                <>
                  This is the name your family will see when they&rsquo;re with you. You
                  can change it later.
                </>
              ) : (
                <>
                  This is the name <strong>on your account</strong> — for our emails to
                  you, and for anyone managing the account with you. It&rsquo;s{' '}
                  <strong>not</strong> the name of a Hearth, and not what your family sees
                  when they&rsquo;re with you.
                </>
              )}
            </p>
          </HCard>

          <HField
            label="Your name"
            id="setup-name"
            type="text"
            placeholder="Joe Smith"
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mb-8"
            autoFocus
          />

          <HButtonPair>
            {isInvited ? (
              <HSecondaryButton onClick={handleSignOut}>Sign out</HSecondaryButton>
            ) : (
              <HSecondaryButton onClick={() => setStep(0)}>Back</HSecondaryButton>
            )}
            <HPrimaryButton onClick={onNameContinue} disabled={isSubmitting || !name.trim()}>
              {isSubmitting ? 'Saving…' : isInvited ? 'Get started' : 'Continue'}
            </HPrimaryButton>
          </HButtonPair>
        </div>

        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          title="Setup error"
          onClose={() => setShowErrorModal(false)}
        />
      </StageShell>
    );
  }

  /* ───────── Step 2 — Name each Hearth (hearthOwner only) ───────── */
  if (step === 2 && !isInvited) {
    const labelFor = (idx) => {
      if (idx === 0) return 'Your home Hearth';
      if (numOfFamilies === 2) return "Your family member's home Hearth";
      return `Hearth #${idx + 1}`;
    };
    // Place-oriented defaults. "Our living room" is a bad default for the
    // home Hearth because that's what families start calling it when
    // they're rushing — we want the name to evoke the household/location so
    // everyone in the Higlo immediately recognises who's home.
    const placeholderFor = (idx) => {
      if (idx === 0) return 'e.g. Jones Family';
      if (idx === 1) return 'e.g. Mum & Dad';
      if (idx === 2) return 'e.g. Aunty Sue';
      return 'e.g. Smith family';
    };
    const helperFor = (idx) => {
      if (idx === 0) return "What others will call you when you're with them.";
      return null;
    };

    return (
      <StageShell
        stageNumber={1}
        stageLabel="Stage 1 of 2"
        stageTitle="Set up your account"
        rightSlot={stageRightSlot}
      >
        <div className="px-4 pb-6">
          <StepCounter index={2} total={4} />
          <SerifHeading size="xl" className="mb-3">
            Name each of your Hearths
          </SerifHeading>

          <HCard variant="muted" className="p-3 mb-4 flex items-center gap-3">
            <HearthIcon size="md" />
            <div className="flex-1 text-[12px] text-slate-600 leading-relaxed">
              A <strong>Hearth</strong> is the physical device that sits above each TV.{' '}
              <strong>One at each home</strong> — give each a name your family will
              recognise.
            </div>
          </HCard>

          {familyNames.map((value, idx) => (
            <HField
              key={idx}
              label={labelFor(idx)}
              helpText={helperFor(idx)}
              id={`setup-hearth-${idx}`}
              type="text"
              placeholder={placeholderFor(idx)}
              value={value}
              onChange={(e) => {
                const next = [...familyNames];
                next[idx] = e.target.value;
                setFamilyNames(next);
              }}
              className="mb-4"
              autoFocus={idx === 0}
            />
          ))}

          <HButtonPair className="mt-4">
            <HSecondaryButton onClick={() => setStep(1)}>Back</HSecondaryButton>
            <HPrimaryButton onClick={onHearthsContinue}>Continue</HPrimaryButton>
          </HButtonPair>
        </div>

        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          title="Setup error"
          onClose={() => setShowErrorModal(false)}
        />
      </StageShell>
    );
  }

  /* ───────── Step 3 — Name first Higlo ───────── */
  if (step === 3 && !isInvited) {
    return (
      <StageShell
        stageNumber={1}
        stageLabel="Stage 1 of 2"
        stageTitle="Set up your account"
        rightSlot={stageRightSlot}
      >
        <div className="px-4 pb-6">
          <StepCounter index={3} total={4} />
          <SerifHeading size="xl" className="mb-3">
            Name your first Higlo
          </SerifHeading>

          <HCard variant="muted" className="p-4 mb-4 flex items-center gap-3">
            <HigloIcon size="md" />
            <div className="flex-1 text-[12px] text-slate-600 leading-relaxed">
              A <strong>Higlo</strong> is a private space your Hearths share. Open your
              Hearth — if anyone else is in this Higlo, you&rsquo;ll see each other. Only
              people you invite are in it.
            </div>
          </HCard>

          <FieldLabel className="mb-1.5">Your Hearths inside</FieldLabel>
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {familyNames.map((n, i) => (
              <HChip key={i} status="on">
                {n || `Hearth ${i + 1}`}
              </HChip>
            ))}
          </div>

          <HField
            label="Give this Higlo a name"
            id="setup-igloo-name"
            type="text"
            placeholder="e.g. Family"
            value={iglooName}
            onChange={(e) => setIglooName(e.target.value)}
            helpText="You can rename it any time, and create more Higlos for different groups later."
            className="mb-8"
            autoFocus
          />

          <HButtonPair>
            <HSecondaryButton onClick={() => setStep(2)}>Back</HSecondaryButton>
            <HPrimaryButton onClick={onHigloContinue}>Continue</HPrimaryButton>
          </HButtonPair>
        </div>

        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          title="Setup error"
          onClose={() => setShowErrorModal(false)}
        />
      </StageShell>
    );
  }

  /* ───────── Step 4 — Navigating Higlos (Normal vs Auto explainer) ─────────
     Sits between First Higlo and Stage-1 done. The home (primary) Hearth
     is left in Normal Mode so the owner sees hand-control training and can
     move between spaces; every additional Hearth defaults to Auto Mode.
     The pill styling matches the in-app HearthCard chip so the language
     reads as familiar.

     This is also where the /user/setup call fires — on "Complete setup",
     not on the previous step's Continue. Doing the heavy work here means
     the spinner sits on the screen the user is already looking at, instead
     of stalling them on the Higlo-name screen. Back to step 3 is fine —
     nothing has been written yet. The next screen (done summary) is the
     one that intentionally has no Back, since the setup has already run. */
  if (step === 4 && !isInvited) {
    return (
      <StageShell
        stageNumber={1}
        stageLabel="Stage 1 of 2"
        stageTitle="Set up your account"
        rightSlot={stageRightSlot}
      >
        <div className="px-4 pb-6">
          <StepCounter index={4} total={4} />
          <SerifHeading size="xl" className="mb-3">
            Navigating Higlos
          </SerifHeading>
          <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
            To make it easy for households that only ever join one Higlo, a Hearth can
            be set to <strong>Auto Mode</strong>: when the lid opens it enters straight
            into the Higlo, no hand controls needed.
          </p>
          <p className="text-[13px] text-slate-600 leading-relaxed mb-5">
            We&rsquo;ve left your Hearth in <strong>Normal Mode</strong> so you&rsquo;ll
            see the hand-control training and can move between spaces. Your other Hearths
            default to <strong>Auto Mode</strong>, you can change this later.
          </p>

          <SectionLabel className="mb-2">Your Hearths</SectionLabel>
          <HCard className="mb-6">
            {familyNames.map((n, idx) => (
              <div
                key={idx}
                className={cn(
                  'px-4 py-3 flex items-center gap-3',
                  idx < familyNames.length - 1 && 'border-b border-slate-100',
                )}
              >
                <div className="w-9 flex justify-center flex-shrink-0">
                  <HearthIcon size="xs" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] font-semibold text-slate-900 truncate">
                    {n || `Hearth ${idx + 1}`}
                  </div>
                </div>
                {idx > 0 && (
                  <HChip className="text-[11px] bg-indigo-50 text-indigo-700 border-indigo-200">
                    Auto Mode
                  </HChip>
                )}
              </div>
            ))}
          </HCard>

          <HButtonPair>
            <HSecondaryButton onClick={() => setStep(3)} disabled={isSubmitting}>
              Back
            </HSecondaryButton>
            <HPrimaryButton onClick={submitSetup} disabled={isSubmitting}>
              {isSubmitting ? 'Setting up…' : 'Complete setup'}
            </HPrimaryButton>
          </HButtonPair>
        </div>

        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          title="Setup error"
          onClose={() => setShowErrorModal(false)}
        />
      </StageShell>
    );
  }

  /* ───────── Step 5 — Stage 1 done summary ───────── */
  if (step === 5 && !isInvited) {
    return (
      <StageShell
        stageNumber={1}
        stageLabel="Stage 1 done"
        stageTitle="Account is set up"
        status="done"
      >
        <div className="px-4 pt-4 pb-6">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 rounded-full flex items-center justify-center bg-emerald-50">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#059669"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          </div>
          <SerifHeading size="xl" className="text-center mb-3">
            Nice work{greetingFirstName ? `, ${greetingFirstName}` : ''}.
          </SerifHeading>
          <p className="text-[14px] text-slate-600 text-center leading-relaxed mb-6 mx-auto max-w-[320px]">
            Your account is ready. Your Hearth{numOfFamilies > 1 ? 's' : ''} and your first
            Higlo have names.
          </p>

          <HCard className="mb-6">
            <div
              className={cn(
                'px-4 py-3.5 flex items-center gap-3',
                'border-b border-slate-100',
              )}
            >
              <div className="w-10 flex justify-center flex-shrink-0">
                <HearthIcon size="xs" />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-slate-900">
                  {numOfFamilies} Hearth{numOfFamilies === 1 ? '' : 's'}
                </div>
                <div className="text-[11px] text-slate-500">
                  {familyNames.filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>
            <div className="px-4 py-3.5 flex items-center gap-3">
              <div className="w-10 flex justify-center flex-shrink-0">
                <HigloIcon size="sm" />
              </div>
              <div className="flex-1">
                <div className="text-[14px] font-semibold text-slate-900">1 Higlo</div>
                <div className="text-[11px] text-slate-500">{iglooName}</div>
              </div>
            </div>
          </HCard>

          <HCard variant="next" className="p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0 bg-slate-900 text-white">
                2
              </div>
              <div className="flex-1 text-[13px] text-slate-800">
                <strong>Next:</strong> set up each Hearth. We&rsquo;ll walk you through —
                they&rsquo;re waiting on the dashboard.
              </div>
            </div>
          </HCard>

          <HPrimaryButton
            onClick={onSetupComplete}
            className="w-full"
            style={{ padding: '14px', fontSize: '15px' }}
          >
            Take me to my Hearths
          </HPrimaryButton>
        </div>
      </StageShell>
    );
  }

  return null;
}

export default AccountSetup;
